import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { formatLocalDate, parseLocalDate } from '../utils/parseLocalDate';

const WEEKDAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const CELL = 40;

const dayKey = (year, month, day) => formatLocalDate(new Date(year, month, day));

const MonthGrid = ({ dates, value, onChange, minDate, maxDate, defaultMonth }) => {
  const byDate = useMemo(() => {
    const map = {};
    dates.forEach((entry) => { map[entry.date] = entry.state; });
    return map;
  }, [dates]);

  const seed = parseLocalDate(value || defaultMonth || minDate) || new Date();
  const [view, setView] = useState({ year: seed.getFullYear(), month: seed.getMonth() });

  const first = new Date(view.year, view.month, 1);
  const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();
  const leading = first.getDay();

  const min = parseLocalDate(minDate);
  const max = parseLocalDate(maxDate);
  const canGoBack = !min || new Date(view.year, view.month, 1) > new Date(min.getFullYear(), min.getMonth(), 1);
  const canGoForward = !max || new Date(view.year, view.month, 1) < new Date(max.getFullYear(), max.getMonth(), 1);

  const step = (delta) => {
    const next = new Date(view.year, view.month + delta, 1);
    setView({ year: next.getFullYear(), month: next.getMonth() });
  };

  const cells = [];
  for (let i = 0; i < leading; i += 1) cells.push(null);
  for (let day = 1; day <= daysInMonth; day += 1) cells.push(day);

  return (
    <div className="sep-cal">
      <div className="sep-cal__head">
        <button
          type="button"
          className="sep-cal__nav"
          onClick={() => step(-1)}
          disabled={!canGoBack}
          aria-label="Previous month"
        >
          &#8249;
        </button>
        <span className="sep-cal__title">{MONTHS[view.month]} {view.year}</span>
        <button
          type="button"
          className="sep-cal__nav"
          onClick={() => step(1)}
          disabled={!canGoForward}
          aria-label="Next month"
        >
          &#8250;
        </button>
      </div>

      <div className="sep-cal__grid" role="grid">
        {WEEKDAYS.map((label) => (
          <span key={label} className="sep-cal__dow">{label}</span>
        ))}

        {cells.map((day, index) => {
          if (day === null) return <span key={`pad-${index}`} className="sep-cal__pad" />;

          const key = dayKey(view.year, view.month, day);
          const state = byDate[key];
          const selected = key === value;

          if (!state) {
            return <span key={key} className="sep-cal__day sep-cal__day--none">{day}</span>;
          }

          const soldOut = state === 'sold_out';

          return (
            <button
              key={key}
              type="button"
              className={[
                'sep-cal__day',
                'sep-cal__day--show',
                soldOut ? 'sep-cal__day--soldout' : 'sep-cal__day--open',
                selected ? 'sep-cal__day--selected' : ''
              ].join(' ').trim()}
              disabled={soldOut}
              aria-pressed={selected}
              aria-label={`${MONTHS[view.month]} ${day}${soldOut ? ', sold out' : ''}`}
              onClick={() => onChange(key)}
            >
              <span className="sep-cal__num">{day}</span>
              <span className="sep-cal__dot" aria-hidden="true" />
            </button>
          );
        })}
      </div>

      <div className="sep-cal__legend">
        <span><i className="sep-cal__dot sep-cal__dot--open" /> Available</span>
        <span><i className="sep-cal__dot sep-cal__dot--soldout" /> Sold Out</span>
      </div>

      <style>{`
        .sep-cal {
          border: 1px solid var(--sep-line, #e5e7eb);
          border-radius: 12px;
          background: var(--sep-surface, #fff);
          padding: 16px;
          max-width: ${CELL * 7 + 32}px;
        }
        .sep-cal__head {
          display: flex; align-items: center; justify-content: space-between;
          margin-bottom: 12px;
        }
        .sep-cal__title { font-size: 16px; font-weight: 700; color: var(--sep-ink, #1a1a1a); }
        .sep-cal__nav {
          width: 30px; height: 30px; border-radius: 50%; cursor: pointer;
          border: 1px solid var(--sep-line, #e5e7eb); background: transparent;
          font-size: 16px; line-height: 1; color: var(--sep-ink, #1a1a1a);
        }
        .sep-cal__nav:disabled { opacity: .35; cursor: default; }
        .sep-cal__grid {
          display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px; justify-items: center;
        }
        .sep-cal__dow {
          font-size: 11px; font-weight: 600; letter-spacing: .04em;
          color: var(--sep-muted, #9ca3af); padding-bottom: 6px;
        }
        .sep-cal__pad { height: ${CELL}px; }
        .sep-cal__day {
          width: ${CELL}px; height: ${CELL}px;
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          font-size: 14px; background: transparent; border: 1px solid transparent;
          border-radius: 8px; padding: 0; color: var(--sep-ink, #1a1a1a);
        }
        .sep-cal__day--none { color: var(--sep-muted, #c7cbd1); }
        .sep-cal__day--show {
          border-color: var(--sep-line-strong, #d1d5db);
          background: var(--sep-surface, #fff);
          font-weight: 600;
        }
        .sep-cal__day--open { cursor: pointer; }
        .sep-cal__day--open:hover { border-color: var(--sep-ink, #1a1a1a); }
        .sep-cal__day--open:focus-visible { outline: 2px solid var(--sep-accent, #2563eb); outline-offset: 1px; }
        .sep-cal__day--soldout { cursor: not-allowed; color: var(--sep-muted, #9ca3af); }
        .sep-cal__day--selected {
          background: var(--sep-accent, #2563eb); border-color: var(--sep-accent, #2563eb); color: #fff;
        }
        .sep-cal__num { line-height: 1; }
        .sep-cal__dot {
          width: 5px; height: 5px; border-radius: 50%; margin-top: 3px;
          background: var(--sep-open, #16a34a); display: block;
        }
        .sep-cal__day--soldout .sep-cal__dot { background: var(--sep-muted, #9ca3af); }
        .sep-cal__day--selected .sep-cal__dot { background: #fff; }
        .sep-cal__legend {
          display: flex; gap: 18px; margin-top: 14px; padding-top: 12px;
          border-top: 1px solid var(--sep-line, #e5e7eb);
          font-size: 13px; color: var(--sep-muted, #6b7280);
        }
        .sep-cal__legend span { display: inline-flex; align-items: center; gap: 6px; }
        .sep-cal__legend i { margin-top: 0; }
        .sep-cal__dot--open { background: var(--sep-open, #16a34a); }
        .sep-cal__dot--soldout { background: var(--sep-muted, #9ca3af); }
      `}</style>
    </div>
  );
};

MonthGrid.propTypes = {
  dates: PropTypes.arrayOf(PropTypes.shape({
    date: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired
  })).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  minDate: PropTypes.string,
  maxDate: PropTypes.string,
  defaultMonth: PropTypes.string
};

export default MonthGrid;
